import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Steps,
  Alert,
  Space,
  Typography,
  Table,
  Tag,
  Progress,
  Result,
  Button,
  Upload,
  Tooltip,
  Row,
  Col,
  message,
  notification,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import type { ColumnsType } from 'antd/es/table';
import apiService from '../../../services/apiService';

const { Title, Text, Paragraph } = Typography;

interface ValidationError {
  row: number;
  field: string;
  value: any;
  error: string;
  severity: 'error' | 'warning';
}

interface PreviewItem {
  row: number;
  orderNumber?: string;
  customer: string;
  customerName: string;
  warehouse: string;
  warehouseName: string;
  productSku: string;
  expectedQuantity: number;
  baseUnit: string;
  packagingUnit: string;
  expectedArrivalDate: string;
  expiryDate?: string;
  logisticsMethod?: string;
  trackingNumber?: string;
  shipperContactName?: string;
  shipperContactPhone?: string;
  cargoNotes?: string;
  status: 'valid' | 'warning' | 'error';
  errors: ValidationError[];
}

const InboundBulkUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewData, setPreviewData] = useState<PreviewItem[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [uploadedFile, setUploadedFile] = useState<any>(null);

  // 字段說明
  const fieldDescriptions = [
    { field: 'customer', name: '客戶', required: true, description: '客戶的識別代碼或編號' },
    { field: 'customerName', name: '客戶名稱', required: true, description: '客戶的完整名稱' },
    { field: 'warehouse', name: '倉庫', required: true, description: '倉庫的識別代碼' },
    { field: 'warehouseName', name: '倉庫名稱', required: true, description: '倉庫的完整名稱' },
    { field: 'productSku', name: 'SKU ID', required: true, description: '產品SKU編碼' },
    { field: 'expectedQuantity', name: '預期數量', required: true, description: '預期入庫的數量' },
    { field: 'baseUnit', name: '基本單位', required: true, description: '計量的基本單位（如：PCS、KG）' },
    { field: 'packagingUnit', name: '包裝單位', required: true, description: '包裝的單位（如：BOX、CASE）' },
    { field: 'expectedArrivalDate', name: '預期到貨日期', required: true, description: '預計貨物到達日期（YYYY-MM-DD格式）' },
    { field: 'expiryDate', name: '到期日期', required: false, description: '產品到期日期（YYYY-MM-DD格式）' },
    { field: 'logisticsMethod', name: '物流方式', required: false, description: '運輸方式（如：DHL、順豐速運）' },
    { field: 'trackingNumber', name: '追蹤號碼', required: false, description: '物流追蹤號碼' },
    { field: 'shipperContactName', name: '發貨聯繫人', required: false, description: '發貨方聯繫人姓名' },
    { field: 'shipperContactPhone', name: '發貨聯繫電話', required: false, description: '發貨方聯繫電話' },
    { field: 'cargoNotes', name: '貨物備註', required: false, description: '關於貨物的額外說明或備註' }
  ];

  // 下載模板
  const downloadTemplate = () => {
    const csvContent = `客戶,客戶名稱,倉庫,倉庫名稱,SKU ID,預期數量,基本單位,包裝單位,預期到貨日期,到期日期,物流方式,追蹤號碼,發貨聯繫人,發貨聯繫電話,貨物備註
CUST001,深圳科技有限公司,WH001,主倉庫,SKU001,100,PCS,BOX,2025-01-15,2026-01-15,DHL,1234567890,張先生,13800000001,電子產品
CUST002,上海貿易公司,WH001,主倉庫,SKU002,200,PCS,BOX,2025-01-16,2026-01-16,順豐速運,SF1234567890,李女士,13800000002,機械配件
CUST003,北京電子集團,WH002,分倉庫,SKU003,150,PCS,BOX,2025-01-17,2026-01-17,京東物流,JD9876543210,王經理,13800000003,消費電子`;

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = '入庫訂單模板.csv';
    link.click();
    URL.revokeObjectURL(link.href);
    message.success('模板文件下載成功');
  };

  // 上傳配置
  const uploadProps = {
    accept: '.csv,.xlsx,.xls',
    showUploadList: false,
    beforeUpload: (file: UploadFile) => {
      setUploadedFile(file);
      processFile(file);
      return false;
    },
  };

  // 處理文件
  const processFile = async (file: UploadFile) => {
    setIsProcessing(true);
    setUploadProgress(0);

    try {
      // 讀取文件內容
      const fileContent = await readFileContent(file);
      setUploadProgress(30);

      // 解析CSV數據
      const parsedData = parseCSVContent(fileContent);
      setUploadProgress(60);

      // 驗證數據
      const validatedData = validateParsedData(parsedData);
      setUploadProgress(90);

      // 生成驗證錯誤列表
      const errors = generateValidationErrors(validatedData);
      
      setPreviewData(validatedData);
      setValidationErrors(errors);
      setUploadProgress(100);
      
      setIsProcessing(false);
      setCurrentStep(1);
    } catch (error) {
      console.error('文件處理錯誤:', error);
      message.error('文件處理失敗，請檢查文件格式');
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  // 讀取文件內容
  const readFileContent = (file: UploadFile): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        resolve(content);
      };
      reader.onerror = () => reject(new Error('文件讀取失敗'));
      reader.readAsText(file as unknown as File, 'UTF-8');
    });
  };

  // 解析CSV內容
  const parseCSVContent = (content: string): any[] => {
    // 移除BOM字符
    content = content.replace(/^\uFEFF/, '');
    
    const lines = content.split('\n').filter(line => line.trim());
    
    if (lines.length < 2) {
      throw new Error('CSV文件格式不正確，至少需要標題行和一行數據');
    }

    const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, '').replace(/\r/g, ''));
    const data: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const row: any = { row: i };
      
      // 即使字段數量不匹配也嘗試解析，只是給出警告
      if (values.length !== headers.length) {
        console.warn(`第${i+1}行字段數量不匹配: 期望${headers.length}個字段，實際${values.length}個字段`);
      }
      
      // 按照標題數量進行映射，缺少的字段用空字符串填充
      headers.forEach((header, index) => {
        const fieldKey = getFieldKey(header);
        const value = (index < values.length ? values[index] : '').trim();
        row[fieldKey] = value;
      });
      
      data.push(row);
    }

    return data;
  };

  // 解析CSV行 - 正確處理空字段
  const parseCSVLine = (line: string): string[] => {
    // 移除行末的換行符
    line = line.replace(/\r?\n$/, '');
    
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    
    while (i <= line.length) {
      const char = i < line.length ? line[i] : ','; // 在結尾添加虛擬逗號來處理最後一個字段
      
      if (char === '"') {
        inQuotes = !inQuotes;
        // 不將引號添加到結果中
      } else if (char === ',' && !inQuotes) {
        // 遇到分隔符，添加當前字段到結果（即使是空的）
        result.push(current.trim());
        current = '';
      } else if (i < line.length) {
        // 普通字符，添加到當前字段
        current += char;
      }
      
      i++;
    }
    
    return result;
  };

  // 字段映射
  const getFieldKey = (header: string): string => {
    const fieldMap: Record<string, string> = {
      '客戶': 'customer',
      '客戶名稱': 'customerName',
      '倉庫': 'warehouse',
      '倉庫名稱': 'warehouseName',
      'SKU ID': 'productSku',
      '預期數量': 'expectedQuantity',
      '基本單位': 'baseUnit',
      '包裝單位': 'packagingUnit',
      '預期到貨日期': 'expectedArrivalDate',
      '到期日期': 'expiryDate',
      '物流方式': 'logisticsMethod',
      '追蹤號碼': 'trackingNumber',
      '發貨聯繫人': 'shipperContactName',
      '發貨聯繫電話': 'shipperContactPhone',
      '貨物備註': 'cargoNotes'
    };
    
    return fieldMap[header] || header;
  };

  // 驗證解析的數據
  const validateParsedData = (data: any[]): PreviewItem[] => {
    return data.map((row, index) => {
      const errors: ValidationError[] = [];
      let status: 'valid' | 'warning' | 'error' = 'valid';

      // 必填字段驗證
      const requiredFields = [
        { key: 'customer', name: '客戶' },
        { key: 'customerName', name: '客戶名稱' },
        { key: 'warehouse', name: '倉庫' },
        { key: 'warehouseName', name: '倉庫名稱' },
        { key: 'productSku', name: 'SKU ID' },
        { key: 'expectedQuantity', name: '預期數量' },
        { key: 'baseUnit', name: '基本單位' },
        { key: 'packagingUnit', name: '包裝單位' },
        { key: 'expectedArrivalDate', name: '預期到貨日期' }
      ];

      requiredFields.forEach(field => {
        if (!row[field.key] || row[field.key].toString().trim() === '') {
          errors.push({
            row: index + 1,
            field: field.key,
            value: row[field.key],
            error: `${field.name}不能為空`,
            severity: 'error'
          });
          status = 'error';
        }
      });

      // 數字字段驗證
      if (row.expectedQuantity && isNaN(Number(row.expectedQuantity))) {
        errors.push({
          row: index + 1,
          field: 'expectedQuantity',
          value: row.expectedQuantity,
          error: '預期數量必須為數字',
          severity: 'error'
        });
        status = 'error';
      }

      // 日期格式驗證
      if (row.expectedArrivalDate && !isValidDate(row.expectedArrivalDate)) {
        errors.push({
          row: index + 1,
          field: 'expectedArrivalDate',
          value: row.expectedArrivalDate,
          error: '日期格式不正確，應為YYYY-MM-DD格式',
          severity: 'error'
        });
        status = 'error';
      }

      // 到期日期格式驗證（可選字段）
      if (row.expiryDate && row.expiryDate.trim() !== '' && !isValidDate(row.expiryDate)) {
        errors.push({
          row: index + 1,
          field: 'expiryDate',
          value: row.expiryDate,
          error: '到期日期格式不正確，應為YYYY-MM-DD格式',
          severity: 'error'
        });
        status = 'error';
      }

      return {
        row: index + 1,
        orderNumber: row.orderNumber || '',
        customer: row.customer || '',
        customerName: row.customerName || '',
        warehouse: row.warehouse || '',
        warehouseName: row.warehouseName || '',
        productSku: row.productSku || '',
        expectedQuantity: Number(row.expectedQuantity) || 0,
        baseUnit: row.baseUnit || '',
        packagingUnit: row.packagingUnit || '',
        expectedArrivalDate: row.expectedArrivalDate || '',
        expiryDate: row.expiryDate || '',
        logisticsMethod: row.logisticsMethod || '',
        trackingNumber: row.trackingNumber || '',
        shipperContactName: row.shipperContactName || '',
        shipperContactPhone: row.shipperContactPhone || '',
        cargoNotes: row.cargoNotes || '',
        status,
        errors
      };
    });
  };

  // 驗證日期格式
  const isValidDate = (dateString: string): boolean => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  // 生成驗證錯誤列表
  const generateValidationErrors = (data: PreviewItem[]): ValidationError[] => {
    const errors: ValidationError[] = [];
    data.forEach(item => {
      errors.push(...item.errors);
    });
    return errors;
  };

  // 生成訂單號
  const generateOrderNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `IB${year}${month}${day}${random}`;
  };

  // 確認導入
  const confirmImport = async () => {
    const validData = previewData.filter(item => item.status === 'valid' || item.status === 'warning');
    
    if (validData.length === 0) {
      message.error('沒有有效的數據可以導入');
      return;
    }

    // 構建後端需要的格式
    const ordersPayload = validData.map(item => ({
      orderNumber: item.orderNumber || generateOrderNumber(),
      customerName: item.customerName,
      warehouseName: item.warehouseName,
      productSku: item.productSku,
      status: 'new',
      expectedQuantity: item.expectedQuantity,
      baseUnit: item.baseUnit,
      packagingUnit: item.packagingUnit,
      expectedArrivalDate: item.expectedArrivalDate,
      expiryDate: item.expiryDate || null,
      logisticsMethod: item.logisticsMethod,
      trackingNumber: item.trackingNumber,
      shipperContactName: item.shipperContactName,
      shipperContactPhone: item.shipperContactPhone,
      cargoRemark: item.cargoNotes
    }));

    try {
      const resp: any = await apiService.bulkCreateInboundOrders(ordersPayload);
      const created = resp?.data || resp?.created || [];
      
      notification.success({
        message: '批量導入成功',
        description: `成功導入 ${created.length || validData.length} 條入庫訂單`,
        placement: 'topRight',
      });

      setCurrentStep(2);
    } catch (error) {
      console.error('批量導入失敗:', error);
      message.error('批量導入失敗，請檢查網絡連接或聯繫技術支持');
    }
  };

  // 表格列配置
  const previewColumns: ColumnsType<PreviewItem> = [
    {
      title: '行號',
      dataIndex: 'row',
      width: 60,
      render: (row: number, record: PreviewItem) => (
        <Tag color={record.status === 'error' ? 'red' : record.status === 'warning' ? 'orange' : 'green'}>
          {row}
        </Tag>
      ),
    },
    {
      title: '客戶',
      dataIndex: 'customerName',
      width: 120,
    },
    {
      title: '倉庫',
      dataIndex: 'warehouseName',
      width: 100,
    },
    {
      title: '預期數量',
      dataIndex: 'expectedQuantity',
      width: 80,
    },
    {
      title: '預期到貨日期',
      dataIndex: 'expectedArrivalDate',
      width: 120,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={status === 'error' ? 'red' : status === 'warning' ? 'orange' : 'green'}>
          {status === 'error' ? '錯誤' : status === 'warning' ? '警告' : '有效'}
        </Tag>
      ),
    },
  ];

  const errorColumns: ColumnsType<ValidationError> = [
    {
      title: '行號',
      dataIndex: 'row',
      width: 60,
    },
    {
      title: '字段',
      dataIndex: 'field',
      width: 100,
    },
    {
      title: '值',
      dataIndex: 'value',
      width: 150,
      render: (value: any) => value || <Text type="secondary">空值</Text>,
    },
    {
      title: '錯誤信息',
      dataIndex: 'error',
      render: (error: string, record: ValidationError) => (
        <Text type={record.severity === 'error' ? 'danger' : 'warning'}>
          {error}
        </Text>
      ),
    },
    {
      title: '嚴重性',
      dataIndex: 'severity',
      width: 80,
      render: (severity: string) => (
        <Tag color={severity === 'error' ? 'red' : 'orange'}>
          {severity === 'error' ? '錯誤' : '警告'}
        </Tag>
      ),
    },
  ];

  const steps = [
    {
      title: '上傳文件',
      description: '選擇並上傳CSV文件',
    },
    {
      title: '數據驗證',
      description: '驗證數據格式和內容',
    },
    {
      title: '導入完成',
      description: '成功導入數據',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 頁面標題 */}
      <div style={{ marginBottom: '24px' }}>
        <Space align="center">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/inventory/inbound')}
          >
            返回
          </Button>
          <Title level={2} style={{ margin: 0 }}>批量上傳入庫訂單</Title>
        </Space>
      </div>

      <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />

      {currentStep === 0 && (
        <div>
          <Alert
            message="批量上傳說明"
            description="現在支持直接上傳CSV文件，系統將自動解析和驗證數據。您也可以下載模板文件作為參考。"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Card title="步驟 1: 下載模板" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                請先下載入庫訂單模板文件，模板包含所有必需的字段和格式說明。支持CSV格式，請確保文件編碼為UTF-8。
              </Paragraph>
              <div style={{ 
                backgroundColor: '#f6ffed', 
                border: '1px solid #b7eb8f', 
                borderRadius: '6px', 
                padding: '12px', 
                marginBottom: '16px' 
              }}>
                <Text strong style={{ color: '#389e0d' }}>✓ 提示</Text>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                  <li>必填字段不能為空，否則會導致驗證失敗</li>
                  <li>日期字段請使用 YYYY-MM-DD 格式（如：2025-01-15）</li>
                  <li>數量字段請輸入有效的數字</li>
                  <li>CSV文件請保存為UTF-8編碼，避免中文亂碼</li>
                </ul>
              </div>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={downloadTemplate}
                size="large"
              >
                下載CSV模板
              </Button>
            </Space>
          </Card>

          <Card title="步驟 2: 上傳文件">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                填寫完成後，請上傳文件進行數據驗證和預覽。
              </Paragraph>
              <Upload {...uploadProps}>
                <Button 
                  icon={<UploadOutlined />} 
                  size="large"
                  loading={isProcessing}
                >
                  {isProcessing ? '處理中...' : '選擇文件上傳'}
                </Button>
              </Upload>
              {uploadedFile && (
                <div>
                  <Text>已選擇文件: {uploadedFile.name}</Text>
                  {isProcessing && (
                    <Progress percent={uploadProgress} style={{ marginTop: 8 }} />
                  )}
                </div>
              )}
            </Space>
          </Card>

          <Card title="字段說明" style={{ marginTop: 16 }}>
            <Row gutter={16}>
              {fieldDescriptions.map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space>
                    <Tag color={field.required ? 'red' : 'default'}>
                      {field.required ? '必填' : '可選'}
                    </Tag>
                    <Text strong>{field.name}</Text>
                  </Space>
                  <div style={{ fontSize: '12px', color: '#666', marginLeft: 16 }}>
                    {field.description}
                  </div>
                </Col>
              ))}
            </Row>
          </Card>


        </div>
      )}

      {currentStep === 1 && (
        <div>
          <Alert
            message={`數據驗證完成，共 ${previewData.length} 行數據`}
            description={
              <Space>
                <span>有效: {previewData.filter(item => item.status === 'valid').length}</span>
                <span>警告: {previewData.filter(item => item.status === 'warning').length}</span>
                <span>錯誤: {previewData.filter(item => item.status === 'error').length}</span>
                <span style={{ marginLeft: '20px', color: '#666' }}>
                  | 按鈕狀態: {validationErrors.filter(e => e.severity === 'error').length > 0 || 
                             previewData.filter(item => item.status === 'valid' || item.status === 'warning').length === 0 
                             ? '🚫 已禁用' : '✅ 可用'}
                </span>
              </Space>
            }
            type={validationErrors.filter(e => e.severity === 'error').length > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 16 }}
          />

          {validationErrors.length > 0 && (
            <Card 
              title={
                <Space>
                  <span>驗證錯誤</span>
                  <Tag color="red">
                    {validationErrors.filter(e => e.severity === 'error').length} 個錯誤
                  </Tag>
                  <Tag color="orange">
                    {validationErrors.filter(e => e.severity === 'warning').length} 個警告
                  </Tag>
                </Space>
              } 
              style={{ marginBottom: 16 }}
            >
              {validationErrors.filter(e => e.severity === 'error').length > 0 && (
                <Alert
                  message="存在錯誤項目"
                  description="請修正所有錯誤項目後才能進行導入。警告項目不會阻止導入，但建議檢查。"
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              <Table
                dataSource={validationErrors}
                columns={errorColumns}
                rowKey={(record, index) => `${record.row}-${record.field}-${index}`}
                size="small"
                pagination={{ pageSize: 5 }}
              />
            </Card>
          )}

          <Card title="數據預覽" extra={
            <Space>
              <Button onClick={() => setCurrentStep(0)}>重新上傳</Button>
              {(() => {
                const hasErrors = validationErrors.filter(e => e.severity === 'error').length > 0;
                const validDataCount = previewData.filter(item => item.status === 'valid' || item.status === 'warning').length;
                const isDisabled = validDataCount === 0 || hasErrors;

                return (
                  <Tooltip 
                    title={
                      hasErrors
                        ? `存在 ${validationErrors.filter(e => e.severity === 'error').length} 個錯誤，請修正後重新上傳`
                        : validDataCount === 0
                        ? '沒有有效的數據可以導入'
                        : '確認導入數據'
                    }
                  >
                    <Button 
                      type={isDisabled ? 'default' : 'primary'}
                      onClick={isDisabled ? undefined : confirmImport}
                      disabled={isDisabled}
                      style={{
                        backgroundColor: isDisabled ? '#f5f5f5' : undefined,
                        borderColor: isDisabled ? '#d9d9d9' : undefined,
                        color: isDisabled ? 'rgba(0, 0, 0, 0.25)' : undefined,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.6 : 1
                      }}
                    >
                      確認導入
                    </Button>
                  </Tooltip>
                );
              })()}
            </Space>
          }>
            <Table
              dataSource={previewData}
              columns={previewColumns}
              rowKey="row"
              size="small"
              pagination={{ pageSize: 10 }}
              rowClassName={(record) => 
                record.status === 'error' ? 'row-error' : 
                record.status === 'warning' ? 'row-warning' : 'row-valid'
              }
            />
          </Card>
        </div>
      )}

      {currentStep === 2 && (
        <Result
          status="success"
          title="批量導入成功！"
          subTitle={`成功導入 ${previewData.filter(item => item.status === 'valid' || item.status === 'warning').length} 個入庫訂單`}
          icon={<CheckCircleOutlined />}
          extra={[
            <Button type="primary" key="back" onClick={() => navigate('/inventory/inbound')}>
              返回入庫管理
            </Button>,
            <Button key="new" onClick={() => {
              setCurrentStep(0);
              setPreviewData([]);
              setValidationErrors([]);
            }}>
              繼續上傳
            </Button>,
          ]}
        />
      )}

      <style>{`
        .row-error {
          background-color: #fff2f0 !important;
        }
        .row-warning {
          background-color: #fffbe6 !important;
        }
        .row-valid {
          background-color: #f6ffed !important;
        }
        
        /* 强制禁用按钮樣式 */
        .ant-btn-primary[disabled], 
        .ant-btn-primary[disabled]:hover, 
        .ant-btn-primary[disabled]:focus, 
        .ant-btn-primary[disabled]:active {
          background-color: #d9d9d9 !important;
          border-color: #d9d9d9 !important;
          color: #666 !important;
          cursor: not-allowed !important;
          opacity: 0.6 !important;
        }
        
        /* 確保禁用狀態下的樣式覆蓋 */
        .ant-btn[disabled] {
          background-color: #f5f5f5 !important;
          border-color: #d9d9d9 !important;
          color: rgba(0, 0, 0, 0.25) !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
        }
      `}</style>
    </div>
  );
};

export default InboundBulkUploadPage;
